import React, { useEffect, useMemo, useState } from "react";
import useApi from "../../CustomHooks/useApi";
import { settlementDraftParticipantSettingsAPI } from "../../../api/settlementDraftParticipantSettingsApi";
import getParticipantStatusStyle from "../../../utils/getParticipantStatusStyle";
import "../../../css/Finance/FullRefundParticipantsSection.css";
import "../../../css/Finance/DraftParticipantSettingsSection.css";

const cleanVariantName = (name) => (name || "Bez wariantu").replace(/^Wycieczka\s*[-–—:]?\s*/i, "");

const groupParticipantsByVariant = (participants) => participants.reduce((acc, participant) => {
  const key = participant.variantGuid || "without-variant";
  if (!acc[key]) acc[key] = { variantGuid: key, variantName: cleanVariantName(participant.variantName), participants: [] };
  acc[key].participants.push(participant);
  return acc;
}, {});

const ParticipantSettingsPopup = ({ isOpen, participants, settings, initialParticipantGuid, onClose, onSave, isLoading }) => {
  const [search, setSearch] = useState("");
  const [selectedParticipantGuid, setSelectedParticipantGuid] = useState("");
  const [isAdministrativeCostExempt, setIsAdministrativeCostExempt] = useState(false);
  const [isExcludedFromAutomaticSettlement, setIsExcludedFromAutomaticSettlement] = useState(false);
  const [notes, setNotes] = useState("");

  const selectedParticipant = participants.find((x) => x.participantGuid === selectedParticipantGuid) || null;
  const selectedExisting = settings.find((x) => x.participantGuid === selectedParticipantGuid);

  useEffect(() => {
    if (!isOpen) return;
    setSearch("");
    setSelectedParticipantGuid(initialParticipantGuid || "");
    const originalBodyOverflow = document.body.style.overflow;
    const originalHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalBodyOverflow;
      document.documentElement.style.overflow = originalHtmlOverflow;
    };
  }, [isOpen, initialParticipantGuid]);

  useEffect(() => {
    if (!selectedParticipantGuid) {
      setIsAdministrativeCostExempt(false);
      setIsExcludedFromAutomaticSettlement(false);
      setNotes("");
      return;
    }
    setIsAdministrativeCostExempt(selectedExisting?.isAdministrativeCostExempt === true);
    setIsExcludedFromAutomaticSettlement(selectedExisting?.isExcludedFromAutomaticSettlement === true);
    setNotes(selectedExisting?.notes || "");
  }, [selectedParticipantGuid, selectedExisting?.isAdministrativeCostExempt, selectedExisting?.isExcludedFromAutomaticSettlement, selectedExisting?.notes]);

  const filteredParticipants = useMemo(() => {
    const phrase = search.trim().toLowerCase();
    if (!phrase) return participants;
    return participants.filter((participant) => [participant.participantName, participant.reservationCode, cleanVariantName(participant.variantName), participant.primaryParticipantName].filter(Boolean).join(" ").toLowerCase().includes(phrase));
  }, [participants, search]);

  const groupedParticipants = useMemo(() => Object.values(groupParticipantsByVariant(filteredParticipants)).sort((a, b) => (a.variantName || "").localeCompare(b.variantName || "", "pl", { sensitivity: "base" })), [filteredParticipants]);
  const selectedParticipantStyle = selectedParticipant ? getParticipantStatusStyle(selectedParticipant.participantStatus) : null;
  const canSave = !!selectedParticipant && (isAdministrativeCostExempt || isExcludedFromAutomaticSettlement) && !isLoading;

  const toggleAdminExemption = () => {
    if (!selectedParticipant) return;
    setIsAdministrativeCostExempt((prev) => {
      const next = !prev;
      if (next) setIsExcludedFromAutomaticSettlement(false);
      return next;
    });
  };

  const toggleAutomaticExclusion = () => {
    if (!selectedParticipant) return;
    setIsExcludedFromAutomaticSettlement((prev) => {
      const next = !prev;
      if (next) setIsAdministrativeCostExempt(false);
      return next;
    });
  };

  if (!isOpen) return null;

  return (
    <div className="full-refund-popup-overlay" onClick={onClose}>
      <div className="full-refund-popup participant-settings-popup" onClick={(e) => e.stopPropagation()}>
        <div className="full-refund-popup-header">
          <div>
            <div className="full-refund-popup-title">Ustaw wyjątek w rozliczeniu osoby</div>
            <div className="full-refund-popup-subtitle">Wybierz osobę, a następnie ustaw zasady rozliczenia.</div>
          </div>
          <div className="full-refund-popup-close" onClick={onClose}><i className="fa-solid fa-xmark" /></div>
        </div>

        <div className="participant-settings-popup-layout">
          <div className="participant-settings-popup-people">
            <div className="full-refund-popup-search">
              <i className="fa-solid fa-magnifying-glass" />
              <input type="text" placeholder="Szukaj po osobie, kodzie lub wariancie" value={search} onChange={(e) => setSearch(e.target.value)} autoFocus />
            </div>

            <div className="full-refund-popup-list participant-settings-popup-list">
              {groupedParticipants.length === 0 ? <div className="draft-empty">Brak osób do pokazania.</div> : groupedParticipants.map((group) => (
                <div key={group.variantGuid} className="full-refund-popup-group">
                  <div className="full-refund-popup-group-title">{group.variantName}</div>
                  <div className="full-refund-popup-group-list">
                    {group.participants.map((participant) => {
                      const participantStyle = getParticipantStatusStyle(participant.participantStatus);
                      const isSelected = selectedParticipantGuid === participant.participantGuid;
                      return (
                        <div key={participant.participantGuid} className={`full-refund-popup-row ${isSelected ? "is-selected" : ""}`} onClick={() => setSelectedParticipantGuid(participant.participantGuid)}>
                          <div className="full-refund-popup-row-left">
                            <div className={`participant-refund-status-icon ${participantStyle.statusClass}`} title={participantStyle.label}><i className={`fa-solid ${participantStyle.icon}`} /></div>
                            <div className="full-refund-popup-row-text">
                              <div className="full-refund-popup-row-name">{participant.participantName || "—"}</div>
                              <div className="full-refund-popup-row-meta">
                                <span className={`reg-code status-${participant.reservationStatus} reg-code-small`}>{participant.reservationCode}</span>
                                <span className="full-refund-popup-row-meta-name">{participant.primaryParticipantName || "—"}</span>
                              </div>
                            </div>
                          </div>
                          <div className="full-refund-popup-row-right"><i className={`fa-solid ${isSelected ? "fa-check" : "fa-chevron-right"}`} /></div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="participant-settings-popup-options">
            {!selectedParticipant ? (
              <div className="participant-settings-popup-placeholder">
                <i className="fa-solid fa-user-check" />
                <strong>Wybierz osobę z listy</strong>
                <span>Dopiero po wybraniu osoby pojawią się dostępne wyjątki.</span>
              </div>
            ) : (
              <>
                <div className="draft-participant-settings-selected">
                  <div className={`participant-refund-status-icon ${selectedParticipantStyle.statusClass}`} title={selectedParticipantStyle.label}><i className={`fa-solid ${selectedParticipantStyle.icon}`} /></div>
                  <div className="draft-participant-settings-selected-text">
                    <strong>{selectedParticipant.participantName}</strong>
                    <div><span className={`reg-code status-${selectedParticipant.reservationStatus} reg-code-small`}>{selectedParticipant.reservationCode}</span><span>{selectedParticipant.primaryParticipantName || "—"}</span></div>
                    <small>{cleanVariantName(selectedParticipant.variantName)}</small>
                  </div>
                </div>

                <div className="draft-participant-settings-rules">
                  <div className={`draft-participant-rule ${isAdministrativeCostExempt ? "active admin" : ""}`} onClick={toggleAdminExemption}>
                    <div className="draft-participant-rule-check">{isAdministrativeCostExempt && <i className="fa-solid fa-check" />}</div>
                    <div><strong>Bez kosztu administracyjnego</strong><span>Osoba nadal bierze udział w podziale, ale nie jest obciążana kosztem administracyjnym.</span></div>
                  </div>
                  <div className={`draft-participant-rule ${isExcludedFromAutomaticSettlement ? "active excluded" : ""}`} onClick={toggleAutomaticExclusion}>
                    <div className="draft-participant-rule-check">{isExcludedFromAutomaticSettlement && <i className="fa-solid fa-check" />}</div>
                    <div><strong>Poza automatycznym rozliczeniem</strong><span>Zwrot wynika wyłącznie z ręcznych korekt. Koszt administracyjny nie jest naliczany; TFG i wykupione NNW pozostają naliczane.</span></div>
                  </div>
                </div>

                <input className="participant-settings-notes" type="text" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notatka (opcjonalnie)" />

                <div className={`draft-participant-settings-save ${!canSave ? "disabled" : ""}`} onClick={() => canSave && onSave({ participantGuid: selectedParticipantGuid, isAdministrativeCostExempt, isExcludedFromAutomaticSettlement, notes: notes.trim() || null })}>
                  {isLoading ? <><i className="fa-solid fa-spinner fa-spin" /><span>Zapisywanie</span></> : <><i className="fa-solid fa-check" /><span>Zapisz wyjątek</span></>}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const DraftParticipantSettingsSection = ({ tripGuid, draft, financialData, onChanged, showToast, clearToast, canEdit }) => {
  const { callApi, isLoading } = useApi();
  const [settings, setSettings] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [editingParticipantGuid, setEditingParticipantGuid] = useState("");

  const participants = useMemo(() => {
    const result = [];
    (financialData?.reservations ?? []).forEach((reservation) => {
      (reservation.participants ?? []).forEach((participant) => {
        const participantGuid = participant.participantGuid || participant.guid;
        if (!participantGuid) return;
        result.push({ participantGuid, participantName: participant.name || "—", participantStatus: participant.status, reservationCode: reservation.reservationCode || "—", reservationStatus: reservation.status, primaryParticipantName: reservation.primaryParticipantName, variantGuid: reservation.variantGuid, variantName: reservation.variantName || "Bez wariantu" });
      });
    });
    return result.sort((a, b) => (a.participantName || "").localeCompare(b.participantName || "", "pl", { sensitivity: "base" }));
  }, [financialData]);

  const fetchSettings = () => {
    if (!draft?.isMine) {
      setSettings([]);
      return Promise.resolve();
    }
    return callApi(() => settlementDraftParticipantSettingsAPI.getAll(tripGuid), (result) => setSettings(result ?? []), () => clearToast?.());
  };

  useEffect(() => { fetchSettings(); }, [tripGuid, draft?.createdByUserId, draft?.isMine]);

  const save = (payload) => {
    if (isLoading) return;
    callApi(
      () => settlementDraftParticipantSettingsAPI.set(tripGuid, payload.participantGuid, { isAdministrativeCostExempt: payload.isAdministrativeCostExempt, isExcludedFromAutomaticSettlement: payload.isExcludedFromAutomaticSettlement, notes: payload.notes }),
      async () => {
        await fetchSettings();
        await onChanged?.();
        setShowPopup(false);
        setEditingParticipantGuid("");
        showToast?.("Zapisano ustawienia rozliczenia osoby", "success");
      },
      () => clearToast?.()
    );
  };

  const edit = (item) => {
    setEditingParticipantGuid(item.participantGuid);
    setShowPopup(true);
  };

  const clear = (item) => {
    if (isLoading) return;
    callApi(
      () => settlementDraftParticipantSettingsAPI.set(tripGuid, item.participantGuid, { isAdministrativeCostExempt: false, isExcludedFromAutomaticSettlement: false, notes: null }),
      async () => {
        await fetchSettings();
        await onChanged?.();
        showToast?.("Usunięto wyjątek rozliczenia osoby", "success");
      },
      () => clearToast?.()
    );
  };

  const adminExemptCount = settings.filter((x) => x.isAdministrativeCostExempt).length;
  const excludedCount = settings.filter((x) => x.isExcludedFromAutomaticSettlement).length;

  return (
    <div className="draft-card draft-participant-settings-card">
      <div className="draft-participant-settings-heading-row">
        <div>
          <div className="draft-card-title">5. Wyjątki w rozliczeniu osób</div>
          <div className="draft-card-subtitle">Specjalne zasady dla pojedynczych osób w tym szkicu. Nie zmieniają danych rezerwacji.</div>
        </div>
        {(adminExemptCount > 0 || excludedCount > 0) && (
          <div className="draft-participant-settings-summary">
            {adminExemptCount > 0 && <span className="admin"><i className="fa-solid fa-receipt" /> Bez admin.: <strong>{adminExemptCount}</strong></span>}
            {excludedCount > 0 && <span className="excluded"><i className="fa-solid fa-user-slash" /> Poza auto.: <strong>{excludedCount}</strong></span>}
          </div>
        )}
      </div>

      {canEdit && draft?.isMine && (
        <div className="draft-participant-settings-toolbar">
          <div className={`full-refund-add-btn ${participants.length === 0 ? "disabled" : ""}`} onClick={() => { if (participants.length > 0) { setEditingParticipantGuid(""); setShowPopup(true); } }}>
            <i className="fa-solid fa-plus" />
            <span>Dodaj wyjątek</span>
          </div>
          <div className="full-refund-picker-count">Wybrane: <strong>{settings.length}</strong></div>
        </div>
      )}

      <div className="draft-participant-settings-list">
        {settings.length === 0 ? <div className="draft-empty">Brak wyjątków dla osób w tym szkicu.</div> : settings.map((item) => {
          const participant = participants.find((x) => x.participantGuid === item.participantGuid);
          const participantStyle = getParticipantStatusStyle(participant?.participantStatus);
          return (
            <div key={item.participantGuid} className="draft-participant-settings-row">
              <div className="draft-participant-settings-person-with-status">
                <div className={`participant-refund-status-icon ${participantStyle.statusClass}`} title={participantStyle.label}><i className={`fa-solid ${participantStyle.icon}`} /></div>
                <div className="draft-participant-settings-person">
                  <strong>{item.participantName}</strong>
                  <div className="draft-participant-settings-person-meta">
                    <span className={`reg-code status-${participant?.reservationStatus} reg-code-small`}>{item.reservationCode}</span>
                    <span>{participant?.primaryParticipantName || "—"}</span>
                  </div>
                  <span>{cleanVariantName(item.variantName)}</span>
                  {item.notes && <small>{item.notes}</small>}
                </div>
              </div>
              <div className="draft-participant-settings-badges">
                {item.isAdministrativeCostExempt && <span className="participant-setting-badge admin"><i className="fa-solid fa-receipt" /> Bez kosztu admin.</span>}
                {item.isExcludedFromAutomaticSettlement && <span className="participant-setting-badge excluded"><i className="fa-solid fa-user-slash" /> Poza auto. rozliczeniem</span>}
              </div>
              {canEdit && draft?.isMine && (
                <div className="draft-participant-settings-actions">
                  <div className="draft-participant-settings-action" onClick={() => edit(item)}><i className="fa-solid fa-pen" /> Edytuj</div>
                  <div className="draft-participant-settings-action danger" onClick={() => clear(item)}><i className="fa-solid fa-trash-can" /> Usuń</div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <ParticipantSettingsPopup isOpen={showPopup} participants={participants} settings={settings} initialParticipantGuid={editingParticipantGuid} onClose={() => { setShowPopup(false); setEditingParticipantGuid(""); }} onSave={save} isLoading={isLoading} />
    </div>
  );
};

export default DraftParticipantSettingsSection;